import { useState, useEffect, useCallback, useMemo } from "react";
import { request } from "../utils/api";
import {
  canViewEmployeeStatistics,
  defaultDashboardTabForRole,
} from "../utils/moduleAccess";
import { useUrlTab } from "./useUrlTab";

const DASHBOARD_TAB_IDS = [
  "Dashboard",
  "PatientStats",
  "Overview",
  "Activity",
];

export const useDashboard = () => {
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("currentUser") || "{}");
    } catch {
      return {};
    }
  }, []);

  const canFetchEmployeeStats = canViewEmployeeStatistics(currentUser);
  const roleDefaultTab = defaultDashboardTabForRole(currentUser);
  const [activeTab, setActiveTab] = useUrlTab(
    DASHBOARD_TAB_IDS,
    roleDefaultTab,
  );

  const [stats, setStats] = useState({
    RN: 0,
    PT: 0,
    PTA: 0,
    OT: 0,
    OTA: 0,
    ST: 0,
    MSW: 0,
    CG: 0,
    CNA: 0,
    HHA: 0,
    LVN: 0,
    OA: 0,
    ADMIN: 0,
    SRVY: 0,
    CON: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    if (!canFetchEmployeeStats) return;
    setLoading(true);
    setError(null);
    try {
      const response = await request("/dashboard");
      if (response && response.status === 1 && response.result) {
        setStats(response.result);
      } else {
        console.warn("Unexpected dashboard response structure:", response);
      }
    } catch (err) {
      // Permission / role denials — do not surface as session expiry
      if (err?.isAuthError) return;
      console.error("Failed to fetch dashboard data:", err);
      setError(err.message || "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, [canFetchEmployeeStats]);

  useEffect(() => {
    if (activeTab === "Dashboard" && canFetchEmployeeStats) {
      fetchDashboardData();
    }
  }, [activeTab, canFetchEmployeeStats, fetchDashboardData]);

  return {
    stats,
    loading,
    error,
    refetch: fetchDashboardData,
    activeTab,
    setActiveTab,
  };
};
